/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { styles } from './themes/PieChartStyles.css';
import {
  getDefaultBorderColor,
  getElementBounds,
  getPieCenter,
  getPieChartInfo,
  getPieSliceGap,
  getRadius,
  getRenderedSlices,
  getSliceInfo
} from './utils';

import { getAvailSpace } from '../utils/PRIVATE_chartUtils/layoutUtils';
import { PieSlice } from './PieSlice';
import { getCenterContext } from './utils';
import { getPieChartNavUtils } from './pieNavUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { ItemInfo, usePieChartNav } from '../hooks/PRIVATE_usePieChartNav';
import { calculateOffset, useDatatip } from '../hooks/PRIVATE_useDatatip';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { TrackResizeContainer } from '../PRIVATE_TrackResizeContainer';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { sizeToCSS } from '../utils/UNSAFE_size';
import type { PieChart, PieChartItem } from './PieChart.types';
import { VisTabularDatatip } from '../PRIVATE_VisTabularDatatip';
import { getColorRamp } from '../utils/UNSAFE_visUtils';

const NUM_PIE_SLICE_THRESHOLD = 100;

export function PieChart({
  data,
  width = '100%',
  height = '448px',
  testId,
  innerRadius = 0,
  ...props
}: PieChart) {
  const colors = getColorRamp();
  const { totalValue, isLabelOutside } = getPieChartInfo(data);
  const sliceInfos = getSliceInfo(data, totalValue);

  // default data item gaps is 0.5. TODO: replace 0.5 with pie dataItemGaps/pieSliceGaps
  const pieSliceGap = getPieSliceGap(0.5);
  const hasLargeSeriesCount = data.length > NUM_PIE_SLICE_THRESHOLD;
  const { direction } = useUser();
  const testIdProps = useTestId(testId);
  return (
    <TrackResizeContainer width={sizeToCSS(width) || '0px'} height={sizeToCSS(height) || '0px'}>
      {function _(width, height) {
        const availSpace = getAvailSpace(width, height);
        const { cx, cy } = getPieCenter(availSpace);
        const radius = getRadius(availSpace, isLabelOutside);
        const ir = innerRadius * radius;
        const renderedSlices = getRenderedSlices(sliceInfos, hasLargeSeriesCount, radius);
        const getNextChartItem = getPieChartNavUtils(renderedSlices, direction === 'rtl');
        const { activeId, navProps, focusedItemInfo, hoveredItemInfo } = usePieChartNav({
          getNextChartItem
        });
        const activeItem = focusedItemInfo.isFocusVisible ? focusedItemInfo : hoveredItemInfo;
        const activeData = activeItem
          ? data[renderedSlices[activeItem.itemIndex].index]
          : undefined;

        const defaultDatatip = (activeData?: PieChartItem, activeItem?: ItemInfo) => {
          if (!activeData) {
            return {
              content: undefined,
              borderColor: undefined
            };
          }

          return {
            content: (
              <VisTabularDatatip
                tableData={[
                  {
                    key: 'Value',
                    value: activeData.value
                  }
                ]}
              />
            ),
            borderColor: getDefaultBorderColor(colors, activeItem, activeData)
          };
        };

        const { content: datatipString, borderColor } =
          activeData && props.datatip
            ? props.datatip({ data: activeData })
            : defaultDatatip(activeData, activeItem);

        const anchor = focusedItemInfo.isFocusVisible ? 'element' : 'pointer';
        const elementBounds = getElementBounds(renderedSlices, cx, cy, ir, radius, activeItem);
        const { datatipContent, datatipProps } = useDatatip({
          content: datatipString,
          offset: calculateOffset(direction === 'rtl', width, elementBounds),
          anchor,
          placement: 'top-start',
          borderColor
        });

        datatipProps['aria-describedby'] = [
          props['aria-describedby'],
          datatipProps['aria-describedby']
        ]
          .filter(Boolean)
          .join(' ');

        const mergedProps = mergeProps(navProps, datatipProps);
        return (
          <div
            tabIndex={0}
            aria-label={props['aria-label']}
            aria-labelledby={props['aria-labelledby']}
            aria-activedescendant={activeId}
            role="application"
            {...mergedProps}
            {...testIdProps}
            className={styles.chart}
            style={{
              width,
              height
            }}>
            {renderedSlices.length > 0 && (
              <svg className={styles.svg}>
                {renderedSlices.map((item, index: number) => {
                  return (
                    <PieSlice
                      cx={cx}
                      cy={cy}
                      itemIndex={index}
                      id={focusedItemInfo.itemIndex === index ? activeId : undefined}
                      isFocused={
                        focusedItemInfo?.itemIndex === index && focusedItemInfo?.isFocusVisible
                      }
                      isHovered={hoveredItemInfo?.itemIndex === index}
                      r={radius}
                      startAngle={item.startAngle}
                      gap={pieSliceGap}
                      innerRadius={ir}
                      angleExtent={item.angleExtent}
                      color={item.color || colors[index % colors.length]}
                      key={item.id}
                      aria-label={item.accessibleLabel}
                    />
                  );
                })}
              </svg>
            )}
            {props.children?.(getCenterContext(cx, cy, ir))}
            {datatipContent}
          </div>
        );
      }}
    </TrackResizeContainer>
  );
}
